import { BlocksOrderedMap, BlockModel } from '@aytm/app/insights/dashboard/models/BlockModel'
import {
  GroupModel,
  GroupListModel,
  ItemListModel,
  ItemTypeModel
} from '@aytm/app/insights/dashboard/models/types.ClientModel'
import { DashboardSchemaModel } from '@aytm/app/insights/dashboard/models/DashboardSchemaModel'
import { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'

import { updateState, updateCurrentData, updateCurrentSchema } from './requests'
import { processItems } from './items'

import { SCHEMA_VERSION } from '@aytm/app/insights/dashboard/lib/config'

export const deleteBlock = (schema: DashboardSchemaModel, uid: string): DashboardSchemaModel => {
  let resultSchema = schema
  const blocks = resultSchema?.get<BlocksOrderedMap>('blocks') || null
  const indexToDelete = blocks?.findIndex(block => block.get<string>('uid') === uid) || -1
  if (0 <= indexToDelete) {
    const newBlocks = blocks.delete(indexToDelete)
    resultSchema = resultSchema.set('blocks', newBlocks)
    const portalUID = blocks.find(block => 'portal' === block.get('purpose'))?.get<string>('uid')
    if (portalUID) {
      resultSchema = resultSchema.set('currentBlockUID', portalUID)
    }
  }
  return resultSchema
}

export const revertBlock = (state: InsightsDashboardPoolState, uid: string): InsightsDashboardPoolState => {
  let resultState = state
  const currentUid = uid || state.currentSchema.get<string>('currentBlockUID')
  const currentBlock = state.currentSchema.getIn<BlockModel>(['blocks', currentUid])
  const remoteBlock = state.remoteSchema.getIn<BlockModel>(['blocks', currentUid])
  if (remoteBlock && currentBlock) {
    const restoredBlock = remoteBlock.set<string>('purpose', currentBlock.get<string>('purpose'))
    const newCurrentSchema = state.currentSchema.setIn(['blocks', currentUid], restoredBlock)
    resultState = updateState(resultState, { currentSchema: newCurrentSchema })
    resultState = updateCurrentData(resultState, {
      panelCurrentFilters: restoredBlock.get('filters'),
      panelFiltersChanged: false
    })
  }
  return resultState
}

export const saveBlock = (state: InsightsDashboardPoolState, block: BlockModel): InsightsDashboardPoolState => {
  const blockUid = block.get<string>('uid')
  const remoteSchema = state.remoteSchema?.updateIn(['blocks', blockUid], (prev: BlockModel) =>
    prev ? prev.merge(block) : block.clone()
  )
  const currentSchema = state.remoteSchema?.updateIn(['blocks', blockUid], (prev: BlockModel) =>
    prev ? prev.merge(block) : block.clone()
  )
  return updateState(state, { remoteSchema: remoteSchema, currentSchema })
}

// TODO: remove deprecated convertSchema function later
export const convertSchema = (state: InsightsDashboardPoolState): InsightsDashboardPoolState => {
  const attributes = state.currentData.attributes || {}
  let newBlocks: BlocksOrderedMap = null
  const blocks = state.currentSchema.get<BlocksOrderedMap>('blocks')
  blocks?.forEach((block: BlockModel) => {
    const version = block.get<string>('version')
    if (version !== SCHEMA_VERSION) {
      const uid = block.get<string>('uid')
      // Correct sort by attribute
      const sortByAttribute = block.get<string>('sortByAttribute') || null
      if (sortByAttribute && !attributes[sortByAttribute]) {
        const attribute = Object.values(attributes).find(
          a => sortByAttribute === a.text || sortByAttribute === a.nativeKey
        )
        if (attribute) {
          newBlocks = (newBlocks || blocks).setIn<string>([uid, 'sortByAttribute'], attribute.key)
        }
      }
      // Correct track attributes
      const trackAttribute = block.getIn<string>(['trackAttributes', 'attribute']) || null
      if (trackAttribute && !attributes[trackAttribute]) {
        const attribute = Object.values(attributes).find(
          a => trackAttribute === a.text || trackAttribute === a.nativeKey
        )
        if (attribute) {
          newBlocks = (newBlocks || blocks).setIn<string>([uid, 'trackAttributes', 'attribute'], attribute.key)
        }
      }
      const trackEntity = block.getIn<string>(['trackAttributes', 'entity']) || null
      if (trackEntity && !attributes[trackEntity]) {
        const entity = Object.values(attributes).find(a => trackEntity === a.text || trackEntity === a.nativeKey)
        if (entity) {
          newBlocks = (newBlocks || blocks).setIn<string>([uid, 'trackAttributes', 'entity'], entity.key)
        }
      }
      // Correct sections
      const sections = block.get<GroupListModel>('sections') || null
      sections?.forEach((section: GroupModel, sectionIndex: number) => {
        // Correct section items
        const items = section.get('items') || null
        items?.forEach((itemUid: string, itemIndex: number) => {
          if (!attributes[itemUid]) {
            const attribute = Object.values(attributes).find(a => itemUid === a.text || itemUid === a.nativeKey)
            if (attribute) {
              newBlocks = (newBlocks || blocks).setIn<string>(
                [uid, 'sections', sectionIndex, 'items', itemIndex],
                attribute.key
              )
            }
          }
        })
        // Delete dublicates in section items
        const uniqueItems = Array.from(new Set(items?.toJS() || []))
        if (uniqueItems.length !== (items?.size || 0)) {
          newBlocks = (newBlocks || blocks).setIn<string[]>([uid, 'sections', sectionIndex, 'items'], uniqueItems)
        }
        // Correct section metrics
        const metrics = section.get('metrics') || null
        metrics?.forEach((metric, metricIndex: number) => {
          const metricUid = metric.get('uid') || null
          const attribute = Object.values(attributes).find(a => metricUid === a.text || metricUid === a.nativeKey)
          if (attribute) {
            newBlocks = (newBlocks || blocks).setIn<string>(
              [uid, 'sections', sectionIndex, 'metrics', metricIndex, 'uid'],
              attribute.key
            )
          }
        })
      })
      // Correct items
      const currentPreset = block.get<string>('currentPreset') || null
      if ('custom' === currentPreset) {
        const newItems = processItems(state, { blockUID: uid, preset: 'All' })
        newBlocks = (newBlocks || blocks).setIn<string>([uid, 'currentPreset'], 'All')
        newBlocks = (newBlocks || blocks).setIn<ItemListModel>([uid, 'items'], newItems)
      }
      // Correct row averages
      const items = block.getBlockItems() || null
      const rowAveragesIndex =
        items?.findIndex((item: ItemTypeModel) => 'avg' === item.get('uid') || item.get('uid').includes('averages')) ||
        -1
      if (0 <= rowAveragesIndex) {
        const item = items.get(rowAveragesIndex) as ItemTypeModel
        if ('avg' !== item.get('uid')) {
          newBlocks = (newBlocks || blocks).setIn<string>([uid, 'items', rowAveragesIndex, 'uid'], 'avg')
        }
        const showRowAverages = block.get<boolean>('showRowAverages') || false
        const rowAveragesPinned = item.get('pinned') || false
        if (showRowAverages !== rowAveragesPinned) {
          newBlocks = (newBlocks || blocks).setIn<boolean>([uid, 'items', rowAveragesIndex, 'pinned'], showRowAverages)
        }
      }
      // Update block version
      newBlocks = (newBlocks || blocks).setIn<string>([uid, 'version'], SCHEMA_VERSION)
    }
  })

  return newBlocks ? updateCurrentSchema(state, { version: SCHEMA_VERSION, blocks: newBlocks }) : state
}
